<div class="space-y-6">
  <!-- Change password card -->
  <div
    class="rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 shadow-sm"
  >
    <div class="p-6 pb-3">
      <h3 class="font-semibold text-lg">{{ 'SETTINGS.SECURITY.CHANGE_PASS' | translate }}</h3>
      <p class="text-sm text-gray-500 dark:text-gray-400 mt-1">
        {{ 'SETTINGS.SECURITY.SEND_OTP_HINT' | translate }}
      </p>
    </div>

    <div class="p-6 pt-0">
      <!-- Error message -->
      @if (errorMsg()) {
        <div
          class="flex items-center gap-2 p-3 mb-4 bg-red-50 dark:bg-red-900/20 text-red-600 rounded-lg text-sm"
        >
          <lucide-icon [img]="AlertCircle" class="w-4 h-4 flex-shrink-0"></lucide-icon>
          {{ errorMsg() }}
        </div>
      }

      <!-- STEP 1: Phone number -->
      @if (step() === 'phone') {
        <div class="space-y-4">
          <!-- Progress indicator -->
          <div class="flex items-center gap-2 mb-5">
            <span
              class="w-7 h-7 rounded-full bg-gray-900 dark:bg-gray-100 text-white dark:text-gray-900 text-xs font-bold flex items-center justify-center"
              >1</span
            >
            <div class="flex-1 h-0.5 bg-gray-200 dark:bg-gray-700"></div>
            <span
              class="w-7 h-7 rounded-full border-2 border-gray-200 dark:border-gray-700 text-gray-400 text-xs font-bold flex items-center justify-center"
              >2</span
            >
          </div>

          <div class="space-y-2">
            <label for="" class="text-sm font-medium text-gray-700 dark:text-gray-300">
              {{ 'SETTINGS.SECURITY.PHONE' | translate }}
            </label>
            <input
              type="tel"
              [(ngModel)]="phoneNumber"
              placeholder="+998901234567"
              class="flex h-10 w-full rounded-md border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gray-950"
            />
            <p class="text-xs text-gray-500 dark:text-gray-400">
              {{ 'SETTINGS.SECURITY.OTP_HINT' | translate }}
            </p>
          </div>

          <div class="flex justify-end pt-2">
            <button
              (click)="sendOtp()"
              [disabled]="isLoading()"
              class="inline-flex items-center justify-center gap-2 rounded-md text-sm font-medium transition-colors bg-gray-900 text-gray-50 dark:bg-gray-100 dark:text-gray-900 hover:bg-gray-900/90 h-10 px-6 hover:cursor-pointer disabled:opacity-50"
            >
              <lucide-icon [img]="Send" class="w-4 h-4"></lucide-icon>
              {{
                isLoading()
                  ? ('SETTINGS.SECURITY.SENDING' | translate)
                  : ('SETTINGS.SECURITY.SEND_CODE' | translate)
              }}
            </button>
          </div>
        </div>
      }

      <!-- STEP 2: OTP + new password -->
      @if (step() === 'otp') {
        <div class="space-y-4">
          <!-- Progress indicator -->
          <div class="flex items-center gap-2 mb-5">
            <span
              class="w-7 h-7 rounded-full bg-emerald-500 text-white text-xs font-bold flex items-center justify-center"
              >✓</span
            >
            <div class="flex-1 h-0.5 bg-gray-900 dark:bg-gray-100"></div>
            <span
              class="w-7 h-7 rounded-full bg-gray-900 dark:bg-gray-100 text-white dark:text-gray-900 text-xs font-bold flex items-center justify-center"
              >2</span
            >
          </div>

          <div
            class="p-3 bg-green-50 dark:bg-green-900/20 rounded-lg text-sm text-green-700 dark:text-green-300"
          >
            {{ 'SETTINGS.SECURITY.CODE_SENT' | translate: { phone: phoneNumber } }}
          </div>

          <!-- OTP -->
          <div class="space-y-2">
            <label for="" class="text-sm font-medium text-gray-700 dark:text-gray-300">
              {{ 'SETTINGS.SECURITY.OTP_LABEL' | translate }}
            </label>
            <input
              type="text"
              [(ngModel)]="otpCode"
              [placeholder]="'SETTINGS.SECURITY.OTP_PLACEHOLDER' | translate"
              maxlength="8"
              class="flex h-10 w-full rounded-md border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 px-3 py-2 text-sm font-mono tracking-widest focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gray-950"
            />
          </div>

          <!-- New password -->
          <div class="space-y-2">
            <label for="" class="text-sm font-medium text-gray-700 dark:text-gray-300">
              {{ 'SETTINGS.SECURITY.NEW_PASSWORD' | translate }}
            </label>
            <div class="relative">
              <input
                [type]="showNewPassword() ? 'text' : 'password'"
                [(ngModel)]="newPassword"
                [placeholder]="'SETTINGS.SECURITY.PASSWORD_MIN' | translate"
                class="flex h-10 w-full rounded-md border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 px-3 py-2 pr-10 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gray-950"
              />
              <button
                type="button"
                (click)="showNewPassword.update((v) => !v)"
                class="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-gray-400 hover:text-gray-600 hover:cursor-pointer"
              >
                @if (showNewPassword()) {
                  <lucide-icon [img]="EyeOff" class="w-4 h-4"></lucide-icon>
                } @else {
                  <lucide-icon [img]="Eye" class="w-4 h-4"></lucide-icon>
                }
              </button>
            </div>
          </div>

          <!-- Confirm password -->
          <div class="space-y-2">
            <label for="" class="text-sm font-medium text-gray-700 dark:text-gray-300">
              {{ 'SETTINGS.SECURITY.CONFIRM_PASSWORD' | translate }}
            </label>
            <div class="relative">
              <input
                [type]="showConfirm() ? 'text' : 'password'"
                [(ngModel)]="confirmPassword"
                [placeholder]="'SETTINGS.SECURITY.CONFIRM_PLACEHOLDER' | translate"
                class="flex h-10 w-full rounded-md border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 px-3 py-2 pr-10 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gray-950"
                [class.border-red-400]="confirmPassword && newPassword !== confirmPassword"
                [class.border-emerald-400]="confirmPassword && newPassword === confirmPassword"
              />
              <button
                type="button"
                (click)="showConfirm.update((v) => !v)"
                class="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-gray-400 hover:text-gray-600 hover:cursor-pointer"
              >
                @if (showConfirm()) {
                  <lucide-icon [img]="EyeOff" class="w-4 h-4"></lucide-icon>
                } @else {
                  <lucide-icon [img]="Eye" class="w-4 h-4"></lucide-icon>
                }
              </button>
            </div>
            @if (confirmPassword && newPassword !== confirmPassword) {
              <p class="text-xs text-red-500">
                {{ 'SETTINGS.SECURITY.PASSWORDS_MISMATCH' | translate }}
              </p>
            }
          </div>

          <div class="flex items-center justify-between pt-2">
            <button
              type="button"
              (click)="restart()"
              class="text-sm text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 hover:cursor-pointer"
            >
              {{ 'SETTINGS.SECURITY.BACK' | translate }}
            </button>
            <button
              (click)="resetPassword()"
              [disabled]="isLoading()"
              class="inline-flex items-center justify-center gap-2 rounded-md text-sm font-medium transition-colors bg-gray-900 text-gray-50 dark:bg-gray-100 dark:text-gray-900 hover:bg-gray-900/90 h-10 px-6 hover:cursor-pointer disabled:opacity-50"
            >
              <lucide-icon [img]="Lock" class="w-4 h-4"></lucide-icon>
              {{
                isLoading()
                  ? ('SETTINGS.SECURITY.UPDATING' | translate)
                  : ('SETTINGS.SECURITY.UPDATE' | translate)
              }}
            </button>
          </div>
        </div>
      }

      <!-- STEP 3: Success -->
      @if (step() === 'success') {
        <div class="flex flex-col items-center gap-4 py-8">
          <div
            class="w-16 h-16 rounded-full bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center"
          >
            <lucide-icon [img]="CheckCircle" class="w-8 h-8 text-emerald-500"></lucide-icon>
          </div>
          <div class="text-center">
            <p class="text-lg font-semibold text-gray-900 dark:text-gray-100">
              {{ 'SETTINGS.SECURITY.SUCCESS_TITLE' | translate }}
            </p>
            <p class="text-sm text-gray-500 dark:text-gray-400 mt-1">
              {{ 'SETTINGS.SECURITY.SUCCESS_SUB' | translate }}
            </p>
          </div>
          <button
            (click)="restart()"
            class="text-sm text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 hover:cursor-pointer"
          >
            {{ 'SETTINGS.SECURITY.CHANGE_AGAIN' | translate }}
          </button>
        </div>
      }
    </div>
  </div>

  <!-- 2FA (static) -->
  <div
    class="rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 shadow-sm"
  >
    <div class="p-6 pb-3">
      <h3 class="font-semibold text-lg">{{ 'SETTINGS.SECURITY.TFA_TITLE' | translate }}</h3>
      <p class="text-sm text-gray-500 dark:text-gray-400 mt-1">
        {{ 'SETTINGS.SECURITY.TFA_SUBTITLE' | translate }}
      </p>
    </div>
    <div class="p-6 pt-0">
      <div class="flex items-center justify-between">
        <div>
          <p class="font-medium text-sm">{{ 'SETTINGS.SECURITY.TFA_LABEL' | translate }}</p>
          <p class="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
            {{ 'SETTINGS.SECURITY.TFA_HINT' | translate }}
          </p>
        </div>
        <input type="checkbox" class="w-5 h-5 accent-gray-900 rounded" />
      </div>
    </div>
  </div>
</div>
